import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { serviceCategoryService } from '../services/serviceCategoryService';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import DeleteConfirmation from '../../../components/common/DeleteConfirmation';

const CategoryForm = ({ category, onClose }) => {
  const isEditing = Boolean(category);
  const queryClient = useQueryClient();
  const [name, setName] = useState(category?.name || '');
  const [sortOrder, setSortOrder] = useState(category?.sort_order ?? 0);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEditing
        ? serviceCategoryService.updateCategory(category.id, payload)
        : serviceCategoryService.createCategory(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      setSuccessMessage(isEditing ? 'Catégorie mise à jour avec succès' : 'Catégorie créée avec succès');
      setTimeout(() => onClose(), 1200);
    },
    onError: (error) => {
      setErrors(error.response?.data?.errors || { general: "Une erreur est survenue." });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    mutation.mutate({ name, sort_order: Number(sortOrder) || 0 });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {successMessage}
            </div>
          )}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Ex: Plomberie"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
            <input
              type="number"
              min="0"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
            {errors.sort_order && <p className="mt-1 text-sm text-red-600">{errors.sort_order[0]}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-50"
            >
              {mutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ServiceCategoryList = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => serviceCategoryService.getCategories(),
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => serviceCategoryService.toggleActive(id),
    onSuccess: () => refetch(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => serviceCategoryService.deleteCategory(id),
    onSuccess: () => {
      refetch();
      setShowDeleteModal(false);
    },
  });

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div className="text-red-500">Erreur lors du chargement des catégories</div>;

  const categories = data?.data || [];

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Catégories de services</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Liste proposée aux prestataires dans l'application mobile (création/modification de service).
            Désactiver une catégorie ne modifie pas les services qui l'utilisent déjà.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4" />
          Nouvelle catégorie
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Nom</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Ordre</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {categories.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{category.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{category.sort_order}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate(category.id)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {category.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      title="Modifier"
                      className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => handleEdit(category)}
                    >
                      <PencilIcon className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      title="Supprimer"
                      className="p-2 hover:bg-red-100 rounded cursor-pointer"
                      onClick={() => handleDelete(category)}
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="4">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucune catégorie pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {showForm && <CategoryForm category={editingCategory} onClose={handleCloseForm} />}

      <DeleteConfirmation
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(selectedCategory?.id)}
        title="Supprimer la catégorie"
        message={`Êtes-vous sûr de vouloir supprimer "${selectedCategory?.name}" ? Les services existants qui l'utilisent ne seront pas affectés.`}
        confirmButtonText="Supprimer"
        confirmButtonColor="danger"
      />
    </div>
  );
};

export default ServiceCategoryList;
